import { focusProfile } from '../../../shared/rules/monk.mjs';
import React from 'react';
import { FaHandSparkles } from 'react-icons/fa';
import { getSpellSlotProfile } from '../../../shared/rules/spellSlots.mjs';
import { getMonkFocusPoints } from '../../../utils/monk';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];

export default function SpellSlots({
  form = {},
  used = {},
  onToggleSlot,
  actionCount: propActionCount,
  bonusCount: propBonusCount,
  reactionSpent,
  showTurnSlots = true,
  showSpellSlots = true,
  spellLabel,
  inlineSpellSlots = false,
  showFocusSlot = true,
  slotProfile,
}) {
  const profile = slotProfile || form.spellSlotProfile || getSpellSlotProfile(form);
  const hasAuthoritativeProfile = Boolean(slotProfile || form.spellSlotProfile);
  const slotData = Object.fromEntries(Object.entries(profile.spellcasting.maximumByLevel).filter(([, count]) => count > 0));
  const warlockData = profile.pactMagic.maximum > 0
    ? { [profile.pactMagic.slotLevel]: profile.pactMagic.maximum } : {};

  const features = form.features || {};
  const monkFocusPoints = getMonkFocusPoints(form);
  const actionCount =
    propActionCount ?? features.actionCount ?? 1;
  const bonusCount =
    propBonusCount ?? features.bonusCount ?? 1;

  const regularLevels = Object.keys(slotData)
    .map(Number)
    .sort((a, b) => a - b);
  const warlockLevels = Object.keys(warlockData)
    .map(Number)
    .sort((a, b) => a - b);

  const renderGroup = (data, type) =>
    Object.keys(data)
      .map(Number)
      .sort((a, b) => a - b)
      .map((lvl) => {
        const count = data[lvl];
        return (
          <div
            key={`${type}-${lvl}`}
            className={`spell-slot ${type === 'warlock' ? 'warlock-slot' : ''}`}
            data-slot-type={type}
            data-slot-level={lvl}
          >
            <div className="slot-level">{ROMAN[lvl - 1] || lvl}</div>
            <div className="slot-boxes">
              {Array.from({ length: count }).map((_, i) => {
                const spent = type === 'regular'
                  ? profile.spellcasting.effectiveSpentByLevel[String(lvl)]
                  : profile.pactMagic.effectiveSpent;
                const state = hasAuthoritativeProfile
                  ? (i < spent ? 'used' : undefined)
                  : used[`${type}-${lvl}`]?.[i];
                const cls =
                  state === 'used' || state === true
                    ? 'slot-used'
                    : state === 'inactive'
                    ? 'slot-inactive'
                    : 'slot-active';
                return (
                  <div
                    key={i}
                    data-slot-index={i}
                    className={`slot-small ${cls}`}
                    aria-label={`${type === 'warlock' ? 'Pact Magic' : 'Spellcasting'} level ${lvl} slot ${i + 1}: ${state === 'used' ? 'spent' : 'available'}`}
                    onClick={() => !hasAuthoritativeProfile && onToggleSlot && onToggleSlot(type, lvl, i)}
                  />
                );
              })}
            </div>
          </div>
        );
      });

  const focusUsed = focusProfile(form).spent;
  const focusRemaining = Math.max(monkFocusPoints - focusUsed, 0);
  const hasMonkFocus = monkFocusPoints > 0;

  return (
    <div
      className="spell-slot-container"
      data-allow-pointer-events="true"
      role="group"
      aria-label="Spell slots and action tracking"
    >
      {showTurnSlots && (
      <div className="spell-slot action-slot">
          <div className="slot-level">A</div>
          <div className="slot-boxes">
            {Array.from({ length: actionCount }).map((_, i) => {
              const state = used.action?.[i];
              const cls = state === 'used' ? 'slot-used' : 'slot-active';
              return (
                <div
                  key={i}
                  data-slot-index={i}
                  data-slot-state={state === 'used' ? 'used' : 'active'}
                  aria-label={`Action ${i + 1}: ${state === 'used' ? 'used' : 'available'}`}
                  className={`action-circle ${cls}`}
                  onClick={() =>
                    onToggleSlot && onToggleSlot('action', i, actionCount)
                  }
                />
              );
            })}
          </div>
        </div>
      )}
      {showTurnSlots && (
      <div className="spell-slot bonus-slot">
        <div className="slot-level">B</div>
        <div className="slot-boxes">
          {Array.from({ length: bonusCount }).map((_, i) => {
            const state = used.bonus?.[i];
            const cls = state === 'used' ? 'slot-used' : 'slot-active';
            return (
              <div
                key={i}
                data-slot-index={i}
                data-slot-state={state === 'used' ? 'used' : 'active'}
                aria-label={`Bonus action ${i + 1}: ${state === 'used' ? 'used' : 'available'}`}
                className={`bonus-circle ${cls}`}
                onClick={() =>
                  onToggleSlot && onToggleSlot('bonus', i, bonusCount)
                }
              />
            );
          })}
        </div>
      </div>
      )}
      {showTurnSlots && reactionSpent !== undefined && (
        <div className="spell-slot reaction-slot">
          <div className="slot-level">R</div>
          <div className="slot-boxes">
            <div data-slot-state={reactionSpent ? 'used' : 'active'}
              aria-label={`Reaction: ${reactionSpent ? 'spent' : 'available'}`}
              className={`reaction-circle ${reactionSpent ? 'slot-used' : 'slot-active'}`} />
          </div>
        </div>
      )}
      {showFocusSlot && hasMonkFocus && (
        <div className="spell-slot focus-slot">
          <div
            className="focus-icon-wrapper"
            role="status"
            aria-label={`Monk's Focus: ${focusRemaining} of ${monkFocusPoints} remaining`}
          >
            <div className="focus-count" aria-hidden="true">
              {focusRemaining}
            </div>
            <FaHandSparkles
              aria-hidden="true"
              className={`focus-icon ${
                focusRemaining > 0 ? 'focus-icon--glow' : ''
              }`}
            />
          </div>
        </div>
      )}
      {showSpellSlots && regularLevels.length > 0 && (
        <div className="spell-slot-section spell-slot-section--regular" aria-label="Prepared spell slots">
          <div className={`spell-slot-section__label ${inlineSpellSlots ? 'spell-slot spell-slot--heading' : ''}`}>{spellLabel || 'Spell Levels'}</div>
          <div className="spell-slot-section__grid">
            {renderGroup(slotData, 'regular')}
            {warlockLevels.length > 0 && renderGroup(warlockData, 'warlock')}
          </div>
        </div>
      )}
      {showSpellSlots && regularLevels.length === 0 && warlockLevels.length > 0 && (
        <div className="spell-slot-section spell-slot-section--pact" aria-label="Pact magic spell slots">
          <div className={`spell-slot-section__label ${inlineSpellSlots ? 'spell-slot spell-slot--heading' : ''}`}>{spellLabel || 'Pact Magic'}</div>
          <div className="spell-slot-section__grid">
            {renderGroup(warlockData, 'warlock')}
          </div>
        </div>
      )}
    </div>
  );
}
